import { useEffect, useRef, useState } from "react";

type Result = {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
};

type Props = {
  onSelect: (lat: number, lng: number, label: string) => void;
  /** Pre-fill the input (e.g. detected location label) */
  currentLabel?: string;
};

// Cincinnati metro viewbox for Nominatim — keeps results local
const VIEWBOX = "-85.0,38.85,-83.9,39.45";

export function AddressSearch({ onSelect, currentLabel }: Props) {
  const [query, setQuery] = useState(currentLabel ?? "");
  const [results, setResults] = useState<Result[]>([]);

  // Sync when parent updates the detected label (e.g. after geolocation)
  useEffect(() => {
    if (currentLabel !== undefined) setQuery(currentLabel);
  }, [currentLabel]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Fetch suggestions with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          q: query,
          format: "json",
          limit: "6",
          countrycodes: "us",
          viewbox: VIEWBOX,
          bounded: "1",
          addressdetails: "0",
        });
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?${params.toString()}`,
          { headers: { "Accept-Language": "en", "User-Agent": "nutoverdose-app/0.1" } },
        );
        const data = (await res.json()) as Result[];
        setResults(data);
        setOpen(data.length > 0);
        setActiveIdx(-1);
      } catch {
        setResults([]);
      }
    }, 320);
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (r: Result) => {
    onSelect(parseFloat(r.lat), parseFloat(r.lon), r.display_name.split(",")[0]);
    setQuery(r.display_name.split(",")[0]);
    setOpen(false);
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      select(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="address-search" ref={wrapperRef}>
      <div className="address-input-wrap">
        <span className="address-icon">🔍</span>
        <input
          type="text"
          className="address-input"
          placeholder="Search Cincinnati address or neighborhood…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={handleKey}
          autoComplete="off"
          spellCheck={false}
        />
        {query && (
          <button
            type="button"
            className="address-clear"
            onClick={() => { setQuery(""); setResults([]); setOpen(false); }}
            aria-label="Clear"
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <ul className="address-dropdown" role="listbox">
          {results.map((r, idx) => {
            const parts = r.display_name.split(", ");
            const primary = parts[0];
            const secondary = parts.slice(1, 3).join(", ");
            return (
              <li
                key={r.place_id}
                role="option"
                aria-selected={idx === activeIdx}
                className={`address-option${idx === activeIdx ? " active" : ""}`}
                onMouseDown={() => select(r)}
              >
                <span className="addr-primary">{primary}</span>
                {secondary && <span className="addr-secondary">{secondary}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
